import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import StatsWidget from "@rio-cloud/rio-uikit/StatsWidget";
import StatsWidgetNumber from "@rio-cloud/rio-uikit/StatsWidgetNumber";
import StatsWidgetSpacer from "@rio-cloud/rio-uikit/StatsWidgetSpacer";

import { fetchMaintenanceStats, fetchMaintenanceDelayed } from "../../api/fetchBigNumbers";
import { IAssetSummary } from "../../api/fetchMaintenance";

import { Asset } from "../../models/asset";
import { getAssetIdsByChassisList } from "../../utils/assetUtils";

const MaintenanceBigNumbers = (
    {
        allAssets,
        assets,
        setAssetIds,
        assetIds,
        filter,
        setFilter,
        toScheduleAssetIds,
        setToScheduleAssetIds,
        delayedMaintenanceAssetIds,
        setDelayedMaintenanceAssetIds,
        scheduledAssetIds,
        setScheduledAssetIds,
        underMaintenanceAssetIds,
        setUnderMaintenanceAssetIds,
        disabled,
        bigTableLoading,
        persona,
    }:
        {
            allAssets: Asset[],
            assets: IAssetSummary[],
            setAssetIds: (e: string[] | undefined) => void,
            assetIds: string[],
            filter: string,
            setFilter: (e: string) => void,
            toScheduleAssetIds: string[],
            setToScheduleAssetIds: (e: string[]) => void,
            delayedMaintenanceAssetIds: string[],
            setDelayedMaintenanceAssetIds: (e: string[]) => void,
            scheduledAssetIds: string[],
            setScheduledAssetIds: (e: string[]) => void,
            underMaintenanceAssetIds: string[],
            setUnderMaintenanceAssetIds: (e: string[]) => void,
            disabled?: boolean,
            bigTableLoading?: boolean,
            persona: 'MANAGER' | 'TOWER' | 'CONSULTANT' | 'PARTNERSHIP',
        }
) => {
    const intl = useIntl();
    const [dataLoaded, setDataLoaded] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(true);
    const [loadingDelayed, setLoadingDelayed] = useState<boolean>(true);
    const [rawUnderMaintenanceCount, setRawUnderMaintenanceCount] = useState<number>(0);

    const [assetIdsWithoutFilter, setAssetIdsWithoutFilter] = useState<string[]>([]);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            const maintenanceStats = fetchMaintenanceStats(persona, allAssets);
            const delayedMaintenanceAssetIds = fetchMaintenanceDelayed(allAssets.map(el => ({ id: el.id, identification: el.chassis })));

            maintenanceStats.then(({ availableToSchedule, scheduled, inProgress, inProgressCount }) => {
                setToScheduleAssetIds(getAssetIdsByChassisList(allAssets, availableToSchedule));
                setUnderMaintenanceAssetIds(getAssetIdsByChassisList(allAssets, inProgress));
                setScheduledAssetIds(getAssetIdsByChassisList(allAssets, scheduled));
                setRawUnderMaintenanceCount(inProgressCount);
                setLoading(false);
                setDataLoaded(true);
            });

            delayedMaintenanceAssetIds.then((delayed) => {
                setDelayedMaintenanceAssetIds(getAssetIdsByChassisList(allAssets, delayed));
                setLoadingDelayed(false);
            });

        }

        if (assetIds.length && allAssets.length && !dataLoaded) fetchData();
        else if (!allAssets.length) {
            setLoading(false);
            setLoadingDelayed(false);
        }
    }, [assetIds, allAssets.length]);

    const getBigNumberValue = (bigNumberAssetIds: string[]): number => {
        // if (!disabled) return bigNumberAssetIds.length;
        const assetIdsSet = new Set(assetIds);
        const bigNumberAssetIdsSet = new Set(bigNumberAssetIds);
        return assetIdsSet.intersection(bigNumberAssetIdsSet).size;
    }

    const getAssetIds = (bigNumberAssetIds: string[]) => {
        return bigNumberAssetIds.filter((id: string) => assetIds.includes(id));
    }

    // const toScheduleAssetIds = assets
    //     .filter((asset: IAssetSummary) => ![...scheduledAssetIds, ...underMaintenanceAssetIds].includes(asset.id))
    //     .map((asset: IAssetSummary) => asset.id);

    return (
        <StatsWidget className="card">
            <StatsWidget.Header>{intl.formatMessage({ id: "intl-msg:monitoring.bigNumber.maintenanceHeader" })}</StatsWidget.Header>
            <StatsWidget.Body className="mt-4">
                <StatsWidgetNumber
                    className={`${loading || bigTableLoading ? `animate-pulse` : ``} ${filter !== '' && filter !== 'toSchedule' ? `` : 'cursor-pointer'}`}
                    value={loading || bigTableLoading ?
                        <div className="width-30 height-40 bg-gray-300 rounded mb-8"></div> :
                        <span className={(filter !== '' && filter !== 'toSchedule') || disabled ? `text-color-lighter` : 'text-color-status-driving'}>{getBigNumberValue(toScheduleAssetIds)}</span>
                    }
                    label={loading || bigTableLoading ?
                        <div className="width-70 h-8 bg-gray-300 rounded"></div> :
                        <span className={(filter !== '' && filter !== 'toSchedule') || disabled ? `text-color-lighter` : ''}>{intl.formatMessage({ id: "intl-msg:monitoring.bigNumber.toSchedule" })}</span>
                    }
                    onClick={() => {
                        if ((filter !== 'toSchedule' && filter !== '') || disabled) return;
                        if (filter !== 'toSchedule') [setAssetIdsWithoutFilter(assets.map(asset => asset.id)), setFilter('toSchedule'), setAssetIds(getAssetIds(toScheduleAssetIds))];
                        else[setFilter(''), setAssetIds(assetIdsWithoutFilter)];
                    }}
                />
                <StatsWidgetSpacer />
                <StatsWidgetNumber
                    className={`${loadingDelayed || bigTableLoading ? `animate-pulse` : ``} ${filter !== '' && filter !== 'delayedMaintenance' ? `` : 'cursor-pointer'}`}
                    value={loadingDelayed || bigTableLoading ?
                        <div className="width-30 height-40 bg-gray-300 rounded mb-8"></div> :
                        <span className={(filter !== '' && filter !== 'delayedMaintenance') || disabled ? `text-color-lighter` : 'text-color-danger'}>{getBigNumberValue(delayedMaintenanceAssetIds)}</span>
                    }
                    label={loadingDelayed || bigTableLoading ?
                        <div className="width-70 h-8 bg-gray-300 rounded"></div> :
                        <span className={(filter !== '' && filter !== 'delayedMaintenance') || disabled ? `text-color-lighter` : ''}>{intl.formatMessage({ id: "intl-msg:monitoring.bigNumber.delayed" })}</span>
                    }
                    onClick={() => {
                        if ((filter !== 'delayedMaintenance' && filter !== '') || disabled) return;
                        if (filter !== 'delayedMaintenance') [setAssetIdsWithoutFilter(assets.map(asset => asset.id)), setFilter('delayedMaintenance'), setAssetIds(getAssetIds(delayedMaintenanceAssetIds))]
                        else[setFilter(''), setAssetIds(assetIdsWithoutFilter)];
                    }}
                />
            </StatsWidget.Body>
            <StatsWidget.Body>
                <StatsWidgetNumber
                    className={`${loading || bigTableLoading ? `animate-pulse` : ``} ${filter !== '' && filter !== 'scheduled' ? `` : 'cursor-pointer'}`}
                    value={loading || bigTableLoading ?
                        <div className="width-30 height-40 bg-gray-300 rounded mb-8"></div> :
                        <span className={(filter !== '' && filter !== 'scheduled') || disabled ? `text-color-lighter` : ''}>{getBigNumberValue(scheduledAssetIds)}</span>
                    }
                    label={loading || bigTableLoading ?
                        <div className="width-100 h-8 bg-gray-300 rounded"></div> :
                        <span className={(filter !== '' && filter !== 'scheduled') || disabled ? `text-color-lighter` : ''}>{intl.formatMessage({ id: "intl-msg:monitoring.bigNumber.scheduled" })}</span>
                    }
                    onClick={() => {
                        if ((filter !== 'scheduled' && filter !== '') || disabled) return;
                        if (filter !== 'scheduled') [setAssetIdsWithoutFilter(assets.map(asset => asset.id)), setFilter('scheduled'), setAssetIds(getAssetIds(scheduledAssetIds))]
                        else[setFilter(''), setAssetIds(assetIdsWithoutFilter)];
                    }}
                />
                <StatsWidgetSpacer />
                <StatsWidgetNumber
                    className={`${loading || bigTableLoading ? `animate-pulse` : ``} ${filter !== '' && filter !== 'underMaintenance' ? `` : 'cursor-pointer'}`}
                    value={loading || bigTableLoading ?
                        <div className="width-30 height-40 bg-gray-300 rounded mb-8"></div> :
                        <span className={(filter !== '' && filter !== 'underMaintenance') || disabled ? `text-color-lighter` : ''}>{(filter !== '' && filter !== 'underMaintenance') ? getBigNumberValue(underMaintenanceAssetIds) : rawUnderMaintenanceCount}</span>
                    }
                    label={loading || bigTableLoading ?
                        <div className="width-100 h-8 bg-gray-300 rounded"></div> :
                        <span className={(filter !== '' && filter !== 'underMaintenance') || disabled ? `text-color-lighter` : ''}>{intl.formatMessage({ id: "intl-msg:monitoring.bigNumber.underMaintenance" })}</span>
                    }
                    onClick={() => {
                        if ((filter !== 'underMaintenance' && filter !== '') || disabled) return;
                        if (filter !== 'underMaintenance') [setAssetIdsWithoutFilter(assets.map(asset => asset.id)), setFilter('underMaintenance'), setAssetIds(getAssetIds(underMaintenanceAssetIds))]
                        else[setFilter(''), setAssetIds(assetIdsWithoutFilter)];
                    }}
                />
            </StatsWidget.Body>
        </StatsWidget>
    )
}

export default MaintenanceBigNumbers;